import { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { INITIAL_LIVES, QUESTIONS } from './questions';
import RunnerGame from './RunnerGame';
import KissAMoleGame from './KissAMoleGame';
import PetDawgGame from './PetDawgGame';
import { fetchTotalAttempts, incrementTotalAttempts } from './supabase';

const SCREENS = {
  SPLASH: 'splash',
  TITLE: 'title',
  PLAY: 'play',
  PRELUDE: 'prelude',
  GAME_OVER: 'game_over',
  WIN: 'win',
  TRAP: 'trap',
};

// After clearing question 22 (index 21), show the trick "win" as question 23.
const TRICK_WIN_AFTER_LEVEL_INDEX = 21;
const TRICK_WIN_QUESTION_NUMBER = 23;
const TRICK_WIN_TIMER_SECONDS = 10;
const TRAP_TIMER_SECONDS = 10;
const QUESTION_24_LEVEL_INDEX = 22;

const DEV_LEVEL_SHORTCUTS_ENABLED = false;
const KEYBOARD_ROWS = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];
const EXPLOSION_HEARTS = Array.from({ length: 32 }, (_, index) => index);
const CONFETTI_PIECES = Array.from({ length: 44 }, (_, index) => index);

let audioContext;
let losingAudio;
let winningAudio;
let winningSoundToken = 0;

const getAudioContext = () => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;

  if (!AudioContext) return null;

  if (!audioContext) {
    audioContext = new AudioContext();
  }

  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {});
  }

  return audioContext;
};

const resumeAudioContext = () => {
  const context = getAudioContext();

  if (!context) return Promise.resolve(null);

  if (context.state === 'running') {
    return Promise.resolve(context);
  }

  return context.resume().then(() => context).catch(() => null);
};

const playTone = ({ frequency, startTime, duration, type = 'sine', volume = 0.08, toneGroup }) => {
  const context = getAudioContext();

  if (!context) return;

  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, startTime);
  gain.gain.setValueAtTime(0.0001, startTime);
  gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(startTime);
  oscillator.stop(startTime + duration + 0.03);

  if (toneGroup) {
    toneGroup.add({ oscillator, gain });
    oscillator.onended = () => {
      toneGroup.forEach((tone) => {
        if (tone.oscillator === oscillator) {
          toneGroup.delete(tone);
        }
      });
    };
  }
};

const playCorrectSound = () => {
  const context = getAudioContext();

  if (!context) return;

  const now = context.currentTime;

  playTone({ frequency: 523.25, startTime: now, duration: 0.12, type: 'triangle' });
  playTone({ frequency: 659.25, startTime: now + 0.09, duration: 0.12, type: 'triangle' });
  playTone({ frequency: 783.99, startTime: now + 0.18, duration: 0.18, type: 'triangle' });
};

const playKeypadSound = () => {
  const context = getAudioContext();

  if (!context) return;

  const now = context.currentTime;

  playTone({ frequency: 880, startTime: now, duration: 0.06, type: 'sine', volume: 0.045 });
  playTone({ frequency: 1320, startTime: now + 0.025, duration: 0.05, type: 'sine', volume: 0.025 });
};

const playWrongSound = () => {
  const context = getAudioContext();

  if (!context) return;

  const now = context.currentTime;
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = 'square';
  oscillator.frequency.setValueAtTime(220, now);
  oscillator.frequency.exponentialRampToValueAtTime(110, now + 0.25);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.06, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(now);
  oscillator.stop(now + 0.3);
};

const playLosingSound = () => {
  if (!losingAudio) {
    losingAudio = new Audio('/audio/lose_song.mp3');
    losingAudio.volume = 0.8;
  }

  losingAudio.pause();
  losingAudio.currentTime = 0;
  losingAudio.play().catch(() => {});
};

const playExplosionSound = () => {
  const context = getAudioContext();

  if (!context) return;

  const now = context.currentTime;
  const bufferSize = context.sampleRate * 0.85;
  const buffer = context.createBuffer(1, bufferSize, context.sampleRate);
  const output = buffer.getChannelData(0);

  for (let i = 0; i < bufferSize; i += 1) {
    const fade = 1 - i / bufferSize;
    output[i] = (Math.random() * 2 - 1) * fade * fade * fade;
  }

  const rumble = context.createBufferSource();
  const rumbleGain = context.createGain();
  const rumbleFilter = context.createBiquadFilter();
  const crackle = context.createBufferSource();
  const crackleGain = context.createGain();
  const crackleFilter = context.createBiquadFilter();
  const drop = context.createOscillator();
  const dropGain = context.createGain();

  rumble.buffer = buffer;
  rumbleFilter.type = 'lowpass';
  rumbleFilter.frequency.setValueAtTime(1300, now);
  rumbleFilter.frequency.exponentialRampToValueAtTime(90, now + 0.7);
  rumbleGain.gain.setValueAtTime(0.28, now);
  rumbleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.85);

  crackle.buffer = buffer;
  crackleFilter.type = 'highpass';
  crackleFilter.frequency.setValueAtTime(1500, now);
  crackleGain.gain.setValueAtTime(0.12, now);
  crackleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);

  drop.type = 'sawtooth';
  drop.frequency.setValueAtTime(95, now);
  drop.frequency.exponentialRampToValueAtTime(38, now + 0.55);
  dropGain.gain.setValueAtTime(0.16, now);
  dropGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.65);

  rumble.connect(rumbleFilter);
  rumbleFilter.connect(rumbleGain);
  rumbleGain.connect(context.destination);
  crackle.connect(crackleFilter);
  crackleFilter.connect(crackleGain);
  crackleGain.connect(context.destination);
  drop.connect(dropGain);
  dropGain.connect(context.destination);

  rumble.start(now);
  rumble.stop(now + 0.85);
  crackle.start(now);
  crackle.stop(now + 0.18);
  drop.start(now);
  drop.stop(now + 0.65);
  playTone({ frequency: 55, startTime: now + 0.04, duration: 0.42, type: 'sawtooth', volume: 0.1 });
  playTone({ frequency: 110, startTime: now + 0.02, duration: 0.16, type: 'square', volume: 0.055 });
};

const playWinningSound = () => {
  const token = ++winningSoundToken;

  if (!winningAudio) {
    winningAudio = new Audio('/audio/win_song.mp3');
    winningAudio.volume = 0.8;
  }

  winningAudio.loop = true;
  winningAudio.pause();
  winningAudio.currentTime = 0;
  winningAudio.play().then(() => {
    // A later stopWinningSound() can race an in-flight play() and leave the loop running.
    if (token !== winningSoundToken) {
      winningAudio.pause();
      winningAudio.currentTime = 0;
      winningAudio.loop = false;
    }
  }).catch(() => {});
};

const stopWinningSound = () => {
  winningSoundToken += 1;

  if (!winningAudio) return;

  winningAudio.loop = false;
  winningAudio.pause();
  winningAudio.currentTime = 0;
};


const playRestartSound = () => {
  const context = getAudioContext();

  if (!context) return;

  const now = context.currentTime;

  playTone({ frequency: 392, startTime: now, duration: 0.1, type: 'sine', volume: 0.065 });
  playTone({ frequency: 523.25, startTime: now + 0.08, duration: 0.1, type: 'sine', volume: 0.065 });
  playTone({ frequency: 659.25, startTime: now + 0.16, duration: 0.14, type: 'sine', volume: 0.065 });
};

function App() {
  const [screen, setScreen] = useState(SCREENS.SPLASH);
  const [levelIndex, setLevelIndex] = useState(0);
  const [lives, setLives] = useState(INITIAL_LIVES);
  const [feedback, setFeedback] = useState(null);
  const [shake, setShake] = useState(false);
  const [sequenceProgress, setSequenceProgress] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [typedAnswer, setTypedAnswer] = useState('');
  const [typingLocked, setTypingLocked] = useState(false);
  const [showHeartExplosion, setShowHeartExplosion] = useState(false);
  const [gameEnding, setGameEnding] = useState(false);
  const [isTrickWin, setIsTrickWin] = useState(false);
  const [scatterLayout, setScatterLayout] = useState([]);
  const [selectedScatterText, setSelectedScatterText] = useState(null);
  const [pressedChoiceText, setPressedChoiceText] = useState(null);
  const [dateComboEntry, setDateComboEntry] = useState([]);
  const [dateComboReturningIds, setDateComboReturningIds] = useState([]);
  const [dateComboFlying, setDateComboFlying] = useState(null);
  const [dateComboSlides, setDateComboSlides] = useState([]);
  const [dateComboSlidingIds, setDateComboSlidingIds] = useState([]);
  const [bombIsGreen, setBombIsGreen] = useState(false);
  const dateComboSlotRefs = useRef([]);
  const dateComboPartRefs = useRef({});
  const dateComboAnimatingRef = useRef(false);
  const bombIsGreenRef = useRef(false);
  const [totalAttempts, setTotalAttempts] = useState(null);
  const openingSongRef = useRef(null);
  const questionTimerRef = useRef({ interval: null, timeout: null, gameOverDelay: null });
  const gameEndingRef = useRef(false);

  const totalLevels = QUESTIONS.length;
  const current = QUESTIONS[levelIndex];
  // Question 23 is the trick win screen (not in QUESTIONS), so Q24 is index 22 → display 24.
  const questionNumber =
    levelIndex > TRICK_WIN_AFTER_LEVEL_INDEX ? levelIndex + 2 : levelIndex + 1;
  const isQuestionNumberAnswer = current.questionNumberCorrect === true;
  const isQuestionNumberTrap = current.questionNumberWrong === true;
  const isQuestionNumberClickable = isQuestionNumberAnswer || isQuestionNumberTrap;
  const sequenceAnswer = current.sequenceAnswer;
  const timerSeconds = current.timerSeconds;
  const typingAnswer = current.typingAnswer;
  const pressButtonText = current.pressButtonText;
  const pressButtonKills = current.pressButtonKills === true;
  const timerAdvances = current.timerAdvances === true;
  const bombColorToggle = current.bombColorToggle === true;
  const showGreenBomb = timerAdvances || (bombColorToggle && bombIsGreen);
  const pantsChoices = current.pantsChoices;
  const scatterChoices = current.scatterChoices;
  const runnerGame = current.runnerGame === true;
  const runnerTargetScore = current.runnerTargetScore || 1000;
  const kissAMoleGame = current.kissAMoleGame === true;
  const kissAMoleTarget = current.kissAMoleTarget || 5;
  const petDawgGame = current.petDawgGame === true;
  const petDawgTarget = current.petDawgTarget || 14;
  const dateCombination = current.dateCombination;
  const dateComboUsedIds = [
    ...dateComboEntry.map((entry) => entry.id),
    ...dateComboReturningIds,
  ];
  const isInteractionLocked =
    feedback !== null ||
    gameEnding ||
    pressedChoiceText !== null ||
    dateComboFlying !== null ||
    dateComboSlides.length > 0 ||
    dateComboSlidingIds.length > 0;
  const activeTimerSeconds =
    screen === SCREENS.TRAP
      ? TRAP_TIMER_SECONDS
      : screen === SCREENS.WIN && isTrickWin
        ? TRICK_WIN_TIMER_SECONDS
        : screen === SCREENS.PLAY
          ? timerSeconds
          : null;
  const fuseProgress =
    timeLeft !== null && activeTimerSeconds
      ? Math.min(
          Math.max((activeTimerSeconds - timeLeft) / Math.max(activeTimerSeconds - 1, 1), 0),
          1
        )
      : 0;
  const visibleChoices = (current.choices || []).filter((choice) => !choice.secret);
  const secretChoices = (current.choices || []).filter((choice) => choice.secret);

  const stopOpeningSong = useCallback(() => {
    if (!openingSongRef.current) return;

    openingSongRef.current.pause();
    openingSongRef.current.currentTime = 0;
  }, []);

  const playOpeningSong = useCallback(() => {
    if (!openingSongRef.current) {
      openingSongRef.current = new Audio('/audio/opening_song.mp3');
      openingSongRef.current.loop = true;
      openingSongRef.current.volume = 0.6;
    }

    return openingSongRef.current.play().catch(() => {});
  }, []);

  const clearQuestionTimer = useCallback(() => {
    window.clearInterval(questionTimerRef.current.interval);
    window.clearTimeout(questionTimerRef.current.timeout);
    window.clearTimeout(questionTimerRef.current.gameOverDelay);
    questionTimerRef.current = { interval: null, timeout: null, gameOverDelay: null };
  }, []);

  useEffect(() => {
    if (screen === SCREENS.TITLE) {
      playOpeningSong();
      return;
    }

    stopOpeningSong();
  }, [playOpeningSong, screen, stopOpeningSong]);

  useEffect(() => {
    if (screen === SCREENS.WIN && !isTrickWin) {
      playWinningSound();
      return () => {
        stopWinningSound();
      };
    }

    stopWinningSound();
    return undefined;
  }, [isTrickWin, screen]);

  useEffect(() => {
    if (screen === SCREENS.WIN && gameEnding) {
      stopWinningSound();
    }
  }, [gameEnding, screen]);

  useEffect(() => {
    setSequenceProgress(0);
    setTypedAnswer('');
    setTypingLocked(false);
    setShowHeartExplosion(false);
    setGameEnding(false);
    gameEndingRef.current = false;
    setPressedChoiceText(null);
    setDateComboEntry([]);
    setDateComboReturningIds([]);
    setDateComboFlying(null);
    setDateComboSlides([]);
    setDateComboSlidingIds([]);
    dateComboAnimatingRef.current = false;
    setBombIsGreen(false);
    bombIsGreenRef.current = false;

    const nextScatterChoices = QUESTIONS[levelIndex]?.scatterChoices;
    if (!nextScatterChoices?.length) {
      setScatterLayout([]);
      setSelectedScatterText(null);
      return;
    }

    const shuffled = [...nextScatterChoices];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    const count = shuffled.length;
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);
    const cellIndexes = Array.from({ length: cols * rows }, (_, index) => index);
    for (let i = cellIndexes.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [cellIndexes[i], cellIndexes[j]] = [cellIndexes[j], cellIndexes[i]];
    }

    const padX = 8;
    const padY = 10;
    const usableW = 100 - padX * 2;
    const usableH = 100 - padY * 2;
    const cellW = usableW / cols;
    const cellH = usableH / rows;

    setScatterLayout(
      shuffled.map((choice, index) => {
        const cell = cellIndexes[index];
        const col = cell % cols;
        const row = Math.floor(cell / cols);
        const jitterX = 0.18 + Math.random() * 0.64;
        const jitterY = 0.18 + Math.random() * 0.64;

        return {
          ...choice,
          left: padX + col * cellW + jitterX * cellW,
          top: padY + row * cellH + jitterY * cellH,
          rotate: Math.random() * 26 - 13,
          fontSize: 0.95 + Math.random() * 0.5,
        };
      })
    );
  }, [levelIndex]);

  useEffect(() => {
    if (screen !== SCREENS.TITLE) return undefined;

    let cancelled = false;

    fetchTotalAttempts().then((attempts) => {
      if (!cancelled && attempts !== null) {
        setTotalAttempts(attempts);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [screen]);

  useEffect(() => {
    clearQuestionTimer();

    if (!activeTimerSeconds) {
      setTimeLeft(null);
      return undefined;
    }

    setTimeLeft(activeTimerSeconds);
    const question = QUESTIONS[levelIndex];

    questionTimerRef.current.interval = window.setInterval(() => {
      setTimeLeft((seconds) => (seconds === null ? seconds : Math.max(seconds - 1, 0)));
    }, 1000);
    questionTimerRef.current.timeout = window.setTimeout(() => {
      clearQuestionTimer();
      setTimeLeft(0);

      const advancesOnTimeout =
        question?.timerAdvances === true ||
        (question?.bombColorToggle === true && bombIsGreenRef.current);

      if (advancesOnTimeout) {
        if (gameEndingRef.current) return;

        playCorrectSound();
        setFeedback('correct');
        window.setTimeout(() => {
          if (gameEndingRef.current) return;

          setFeedback(null);
          const isLast = levelIndex >= QUESTIONS.length - 1;
          if (isLast) {
            setIsTrickWin(false);
            setScreen(SCREENS.WIN);
            return;
          }

          const nextLevelIndex = levelIndex + 1;
          setLevelIndex(nextLevelIndex);
          if (QUESTIONS[nextLevelIndex].preludeText) {
            setScreen(SCREENS.PRELUDE);
          }
        }, 650);
        return;
      }

      setLives(0);
      setFeedback(null);
      setShake(false);
      setSequenceProgress(0);
      setTypedAnswer('');
      setTypingLocked(false);
      setGameEnding(true);
      gameEndingRef.current = true;
      setShowHeartExplosion(true);
      stopWinningSound();
      playExplosionSound();
      setShake(true);
      window.setTimeout(() => setShake(false), 650);
      questionTimerRef.current.gameOverDelay = window.setTimeout(() => {
        setShowHeartExplosion(false);
        playLosingSound();
        setScreen(SCREENS.GAME_OVER);
      }, 950);
    }, activeTimerSeconds * 1000);

    return clearQuestionTimer;
  }, [activeTimerSeconds, clearQuestionTimer, levelIndex, screen]);

  const triggerBombGameOver = useCallback(() => {
    if (gameEndingRef.current || screen !== SCREENS.PLAY) return;

    clearQuestionTimer();
    setLives(0);
    setFeedback(null);
    setShake(false);
    setSequenceProgress(0);
    setTypedAnswer('');
    setTypingLocked(false);
    setGameEnding(true);
    gameEndingRef.current = true;
    setShowHeartExplosion(true);
    stopWinningSound();
    playExplosionSound();
    setShake(true);
    window.setTimeout(() => setShake(false), 650);
    questionTimerRef.current.gameOverDelay = window.setTimeout(() => {
      setShowHeartExplosion(false);
      playLosingSound();
      setScreen(SCREENS.GAME_OVER);
    }, 950);
  }, [clearQuestionTimer, screen]);

  useEffect(() => {
    if (!DEV_LEVEL_SHORTCUTS_ENABLED) return undefined;

    const onPlayScreen = screen === SCREENS.PLAY;
    const onTrickWin = screen === SCREENS.WIN && isTrickWin;
    if (!onPlayScreen && !onTrickWin) return undefined;

    const resetDevNavState = () => {
      setFeedback(null);
      setShake(false);
      setSequenceProgress(0);
      setTypedAnswer('');
      setTypingLocked(false);
      setShowHeartExplosion(false);
      setGameEnding(false);
      gameEndingRef.current = false;
    };

    const enterTrickWin = () => {
      resetDevNavState();
      clearQuestionTimer();
      setTimeLeft(null);
      setLevelIndex(TRICK_WIN_AFTER_LEVEL_INDEX);
      setIsTrickWin(true);
      setScreen(SCREENS.WIN);
    };

    const handleDevLevelShortcut = (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

      event.preventDefault();

      if (onTrickWin) {
        resetDevNavState();
        stopWinningSound();
        setIsTrickWin(false);
        clearQuestionTimer();
        setTimeLeft(null);
        if (event.key === 'ArrowRight') {
          setLevelIndex(QUESTION_24_LEVEL_INDEX);
        } else {
          setLevelIndex(TRICK_WIN_AFTER_LEVEL_INDEX);
        }
        setScreen(SCREENS.PLAY);
        return;
      }

      if (event.key === 'ArrowRight' && levelIndex === TRICK_WIN_AFTER_LEVEL_INDEX) {
        enterTrickWin();
        return;
      }

      if (event.key === 'ArrowLeft' && levelIndex === QUESTION_24_LEVEL_INDEX) {
        enterTrickWin();
        return;
      }

      resetDevNavState();
      setLevelIndex((index) => {
        if (event.key === 'ArrowRight') {
          return Math.min(index + 1, totalLevels - 1);
        }

        return Math.max(index - 1, 0);
      });
    };

    window.addEventListener('keydown', handleDevLevelShortcut);

    return () => {
      window.removeEventListener('keydown', handleDevLevelShortcut);
    };
  }, [clearQuestionTimer, isTrickWin, levelIndex, screen, totalLevels]);

  const startGame = useCallback(() => {
    stopOpeningSong();
    stopWinningSound();
    playRestartSound();

    incrementTotalAttempts().then((attempts) => {
      if (attempts !== null) {
        setTotalAttempts(attempts);
      }
    });

    setLevelIndex(0);
    setLives(INITIAL_LIVES);
    setFeedback(null);
    setSequenceProgress(0);
    setTypedAnswer('');
    setTypingLocked(false);
    setTimeLeft(null);
    setShowHeartExplosion(false);
    setGameEnding(false);
    gameEndingRef.current = false;
    setIsTrickWin(false);
    clearQuestionTimer();
    setScreen(SCREENS.PLAY);
  }, [clearQuestionTimer, stopOpeningSong]);

  const continueFromTrickWin = useCallback(() => {
    playCorrectSound();
    stopWinningSound();
    setIsTrickWin(false);
    setFeedback(null);
    setSequenceProgress(0);
    setTypedAnswer('');
    setTypingLocked(false);
    setTimeLeft(null);
    setShowHeartExplosion(false);
    setGameEnding(false);
    gameEndingRef.current = false;
    clearQuestionTimer();
    setLevelIndex(QUESTION_24_LEVEL_INDEX);
    setScreen(SCREENS.PLAY);
  }, [clearQuestionTimer]);

  const handleChoice = (correct) => {
    if (feedback !== null || gameEndingRef.current || screen !== SCREENS.PLAY) return;

    if (correct) {
      clearQuestionTimer();
      playCorrectSound();
      setFeedback('correct');
      const isLast = levelIndex >= totalLevels - 1;
      const isTrickWinLevel = levelIndex === TRICK_WIN_AFTER_LEVEL_INDEX;
      window.setTimeout(() => {
        if (gameEndingRef.current) return;

        setFeedback(null);
        if (isTrickWinLevel) {
          setIsTrickWin(true);
          setScreen(SCREENS.WIN);
        } else if (isLast) {
          setIsTrickWin(false);
          setScreen(SCREENS.WIN);
        } else {
          const nextLevelIndex = levelIndex + 1;
          setLevelIndex(nextLevelIndex);
          if (QUESTIONS[nextLevelIndex].preludeText) {
            setScreen(SCREENS.PRELUDE);
          }
        }
      }, 650);
      return;
    }

    playWrongSound();
    setFeedback('wrong');
    setShake(true);
    window.setTimeout(() => setShake(false), 450);

    const nextLives = lives - 1;
    if (nextLives <= 0) {
      setGameEnding(true);
      gameEndingRef.current = true;
    }

    window.setTimeout(() => {
      setFeedback(null);
      if (nextLives <= 0) {
        playLosingSound();
        setScreen(SCREENS.GAME_OVER);
      } else {
        setLives(nextLives);
      }
    }, 700);
  };

  const handleAnswerClick = (choice) => {
    if (!sequenceAnswer) {
      if (choice.trapEnding) {
        if (feedback !== null || gameEndingRef.current || screen !== SCREENS.PLAY || pressedChoiceText) {
          return;
        }
        clearQuestionTimer();
        playKeypadSound();
        setPressedChoiceText(choice.text);
        window.setTimeout(() => {
          if (gameEndingRef.current) return;
          setPressedChoiceText(null);
          setScreen(SCREENS.TRAP);
        }, 280);
        return;
      }

      handleChoice(choice.correct);
      return;
    }

    const expected = sequenceAnswer[sequenceProgress];

    if (choice.text !== expected) {
      setSequenceProgress(0);
      handleChoice(false);
      return;
    }

    const nextProgress = sequenceProgress + 1;
    playKeypadSound();

    if (nextProgress === sequenceAnswer.length) {
      setSequenceProgress(0);
      clearQuestionTimer();
      handleChoice(true);
      return;
    }

    setSequenceProgress(nextProgress);
  };

  const finishDateComboIfComplete = (nextEntry) => {
    if (!dateCombination || nextEntry.length < dateCombination.answer.length) {
      dateComboAnimatingRef.current = false;
      return;
    }

    const isCorrect = nextEntry.every(
      (entry, index) => entry.value === dateCombination.answer[index]
    );

    if (isCorrect) {
      clearQuestionTimer();
      handleChoice(true);
      dateComboAnimatingRef.current = false;
      return;
    }

    const returnFlights = nextEntry
      .map((entry, index) => {
        const fromNode = dateComboSlotRefs.current[index];
        const toNode = dateComboPartRefs.current[entry.id];
        const from = fromNode?.getBoundingClientRect();
        const to = toNode?.getBoundingClientRect();
        if (!from || !to) return null;
        return {
          id: entry.id,
          value: entry.value,
          from: {
            left: from.left,
            top: from.top,
            width: from.width,
            height: from.height,
          },
          to: {
            left: to.left,
            top: to.top,
            width: to.width,
            height: to.height,
          },
        };
      })
      .filter(Boolean);

    dateComboAnimatingRef.current = true;
    setDateComboReturningIds(nextEntry.map((entry) => entry.id));
    setDateComboSlidingIds(nextEntry.map((entry) => entry.id));
    setDateComboEntry([]);
    setDateComboSlides(returnFlights);
    handleChoice(false);

    window.setTimeout(() => {
      setDateComboSlides([]);
      setDateComboSlidingIds([]);
      setDateComboReturningIds([]);
      dateComboAnimatingRef.current = false;
    }, 340);
  };

  const handleDateComboValue = (partId, value, event) => {
    if (!dateCombination || isInteractionLocked || dateComboAnimatingRef.current) return;
    if (dateComboUsedIds.includes(partId)) return;
    if (dateComboEntry.length >= dateCombination.answer.length) return;

    const sourceRect = event.currentTarget.getBoundingClientRect();
    const slotIndex = dateComboEntry.length;
    const targetNode = dateComboSlotRefs.current[slotIndex];
    const targetRect = targetNode?.getBoundingClientRect();

    playKeypadSound();
    dateComboAnimatingRef.current = true;

    if (!targetRect) {
      const nextEntry = [...dateComboEntry, { id: partId, value }];
      setDateComboEntry(nextEntry);
      finishDateComboIfComplete(nextEntry);
      return;
    }

    setDateComboFlying({
      value,
      from: {
        left: sourceRect.left,
        top: sourceRect.top,
        width: sourceRect.width,
        height: sourceRect.height,
      },
      to: {
        left: targetRect.left,
        top: targetRect.top,
        width: targetRect.width,
        height: targetRect.height,
      },
    });
    setDateComboReturningIds((ids) => [...ids, partId]);

    window.setTimeout(() => {
      setDateComboFlying(null);
      setDateComboReturningIds((ids) => ids.filter((id) => id !== partId));
      setDateComboEntry((currentEntry) => {
        const nextEntry = [...currentEntry, { id: partId, value }];
        window.setTimeout(() => finishDateComboIfComplete(nextEntry), 20);
        return nextEntry;
      });
    }, 320);
  };

  const handleDateComboRemove = (slotIndex) => {
    if (!dateCombination || isInteractionLocked || dateComboAnimatingRef.current) return;
    if (slotIndex < 0 || slotIndex >= dateComboEntry.length) return;

    const entry = dateComboEntry[slotIndex];
    const slotNode = dateComboSlotRefs.current[slotIndex];
    const homeNode = dateComboPartRefs.current[entry.id];
    if (!slotNode || !homeNode) return;

    const fromRect = slotNode.getBoundingClientRect();
    const toRect = homeNode.getBoundingClientRect();

    const slides = dateComboEntry.slice(slotIndex + 1).map((item, offset) => {
      const fromSlot = dateComboSlotRefs.current[slotIndex + 1 + offset];
      return {
        id: item.id,
        value: item.value,
        from: fromSlot?.getBoundingClientRect(),
        toIndex: slotIndex + offset,
      };
    });

    playKeypadSound();
    dateComboAnimatingRef.current = true;
    setDateComboReturningIds((ids) => [...ids, entry.id]);
    setDateComboSlidingIds(slides.map((slide) => slide.id));
    setDateComboEntry((current) => current.filter((_, index) => index !== slotIndex));

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const resolvedSlides = slides
          .map((slide) => {
            const toNode = dateComboSlotRefs.current[slide.toIndex];
            const to = toNode?.getBoundingClientRect();
            if (!slide.from || !to) return null;
            return {
              id: slide.id,
              value: slide.value,
              from: {
                left: slide.from.left,
                top: slide.from.top,
                width: slide.from.width,
                height: slide.from.height,
              },
              to: {
                left: to.left,
                top: to.top,
                width: to.width,
                height: to.height,
              },
            };
          })
          .filter(Boolean);

        setDateComboSlides(resolvedSlides);
        setDateComboFlying({
          value: entry.value,
          from: {
            left: fromRect.left,
            top: fromRect.top,
            width: fromRect.width,
            height: fromRect.height,
          },
          to: {
            left: toRect.left,
            top: toRect.top,
            width: toRect.width,
            height: toRect.height,
          },
        });
      });
    });

    window.setTimeout(() => {
      setDateComboFlying(null);
      setDateComboSlides([]);
      setDateComboSlidingIds([]);
      setDateComboReturningIds((ids) => ids.filter((id) => id !== entry.id));
      dateComboAnimatingRef.current = false;
    }, 340);
  };

  const handleTypingLetter = (letter) => {
    if (!typingAnswer || feedback !== null || typingLocked) return;

    const normalizedLetter = letter.toUpperCase();
    const expectedLetter = typingAnswer[typedAnswer.length].toUpperCase();

    if (normalizedLetter !== expectedLetter) {
      setTypedAnswer('');
      handleChoice(false);
      return;
    }

    const nextTypedAnswer = `${typedAnswer}${normalizedLetter}`;
    playKeypadSound();
    setTypedAnswer(nextTypedAnswer);

    if (nextTypedAnswer.length === typingAnswer.length) {
      setTypingLocked(true);
      clearQuestionTimer();
      window.setTimeout(() => {
        setTypingLocked(false);
        handleChoice(true);
      }, 1000);
    }
  };

  if (screen === SCREENS.SPLASH) {
    return (
      <div className="game-root" style={{ cursor: 'pointer' }} onClick={() => {
        resumeAudioContext().then(() => {
          playOpeningSong();
          setScreen(SCREENS.TITLE);
        });
      }}>
        <div className="panel title-panel">
          <p className="eyebrow">A very serious quiz</p>
          <h1 className="game-title">Vale Quiz</h1>
          <p className="subtitle">
            Click anywhere to start
          </p>
        </div>
      </div>
    );
  }

  if (screen === SCREENS.TITLE) {
    return (
      <div className="game-root">
        <div className="panel title-panel">
          <p className="eyebrow">A very serious quiz</p>
          <h1 className="game-title">Vale Quiz</h1>
          <p className="subtitle">
            You have {INITIAL_LIVES} lives. Wrong answers cost a life. Run out and you start from
            level 1 again.
          </p>
          <button type="button" className="btn btn-primary" onClick={startGame}>
            Start
          </button>
          <p className="attempts-counter">
            Total Attempts Taken:{' '}
            {totalAttempts === null ? '—' : totalAttempts.toLocaleString()}
          </p>
        </div>
      </div>
    );
  }

  if (screen === SCREENS.GAME_OVER) {
    return (
      <div className="game-root">
        <div className="panel game-over-panel">
          <h2 className="panel-title">Out of lives</h2>
          <p className="subtitle">Keep on trying! You only lose when you give up.</p>
          <button type="button" className="btn btn-primary" onClick={startGame}>
            Try again from level 1
          </button>
        </div>
      </div>
    );
  }

  if (screen === SCREENS.PRELUDE) {
    return (
      <div className="game-root prelude-root" onClick={() => setScreen(SCREENS.PLAY)}>
        <div className="panel prelude-panel">
          <p className="prelude-text">{current.preludeText}</p>
          <p className="prelude-hint">Click anywhere to continue</p>
        </div>
      </div>
    );
  }

  if (screen === SCREENS.TRAP) {
    return (
      <div className={`game-root ${shake ? 'shake' : ''}`}>
        <header className="hud">
          <span className="hud-level">Question {questionNumber}</span>
          {timeLeft !== null && (
            <span
              className={`hud-timer ${timeLeft <= 3 ? 'hud-timer-warning' : ''}`}
              style={{
                '--fuse-scale': 1 - fuseProgress,
                '--spark-x': `${fuseProgress * -1.05}rem`,
              }}
            >
              <span className="hud-timer-wick" />
              <span className="hud-timer-spark">✦</span>
              <span className="hud-timer-count">{timeLeft}</span>
            </span>
          )}
        </header>
        <footer className="lives-bar">
          <span className="hud-lives" aria-label={`${lives} lives remaining`}>
            {Array.from({ length: INITIAL_LIVES }, (_, i) => (
              <span key={i} className={`life ${i < lives ? 'life-full' : 'life-empty'}`}>
                ♥
              </span>
            ))}
          </span>
        </footer>
        <div className="panel trap-panel">
          <p className="trap-text">No cutting corners, play my damn game!</p>
        </div>
        {showHeartExplosion && (
          <div className="heart-explosion" aria-hidden="true">
            {EXPLOSION_HEARTS.map((heart) => (
              <span key={heart} className="heart-explosion-heart">
                ♥
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (screen === SCREENS.WIN) {
    return (
      <div className={`game-root ${shake ? 'shake' : ''}`}>
        {isTrickWin && (
          <>
            <header className="hud">
              <span className="hud-level">Question {TRICK_WIN_QUESTION_NUMBER}</span>
              {timeLeft !== null && (
                <span
                  className={`hud-timer ${timeLeft <= 3 ? 'hud-timer-warning' : ''}`}
                  style={{
                    '--fuse-scale': 1 - fuseProgress,
                    '--spark-x': `${fuseProgress * -1.05}rem`,
                  }}
                >
                  <span className="hud-timer-wick" />
                  <span className="hud-timer-spark">✦</span>
                  <span className="hud-timer-count">{timeLeft}</span>
                </span>
              )}
            </header>
            <footer className="lives-bar">
              <span className="hud-lives" aria-label={`${lives} lives remaining`}>
                {Array.from({ length: INITIAL_LIVES }, (_, i) => (
                  <span key={i} className={`life ${i < lives ? 'life-full' : 'life-empty'}`}>
                    ♥
                  </span>
                ))}
              </span>
            </footer>
          </>
        )}
        <div className="confetti" aria-hidden="true">
          {CONFETTI_PIECES.map((piece) => (
            <span key={piece} className="confetti-piece" />
          ))}
        </div>
        <div className="panel win-panel">
          <h2 className="panel-title">You did it</h2>
          <p className="subtitle">You finally came to your senses. Happy birthday my love!</p>
          <button
            type="button"
            className="btn btn-primary"
            disabled={isTrickWin && isInteractionLocked}
            onClick={() => {
              stopWinningSound();
              if (isTrickWin) {
                continueFromTrickWin();
                return;
              }
              startGame();
            }}
          >
            Play again
          </button>
        </div>
        {showHeartExplosion && (
          <div className="heart-explosion" aria-hidden="true">
            {EXPLOSION_HEARTS.map((heart) => (
              <span key={heart} className="heart-explosion-heart">
                ♥
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`game-root ${shake ? 'shake' : ''}`}>
      <header className="hud">
        <span className="hud-level">
          Question{' '}
          {isQuestionNumberClickable ? (
            <button
              type="button"
              className="hud-question-number"
              disabled={isInteractionLocked}
              onClick={() => handleChoice(isQuestionNumberAnswer)}
            >
              {questionNumber}
            </button>
          ) : (
            <span>{questionNumber}</span>
          )}
        </span>
        {timeLeft !== null && (
          <span
            className={`hud-timer ${showGreenBomb ? 'hud-timer-green' : ''} ${
              timeLeft <= (showGreenBomb ? 1 : 3) ? 'hud-timer-warning' : ''
            }`}
            style={{
              '--fuse-scale': 1 - fuseProgress,
              '--spark-x': `${fuseProgress * -1.05}rem`,
            }}
          >
            <span className="hud-timer-wick" />
            <span className="hud-timer-spark">✦</span>
            <span className="hud-timer-count">{timeLeft}</span>
          </span>
        )}
      </header>

      <footer className="lives-bar">
        <span className="hud-lives" aria-label={`${lives} lives remaining`}>
          {Array.from({ length: INITIAL_LIVES }, (_, i) => (
            <span key={i} className={`life ${i < lives ? 'life-full' : 'life-empty'}`}>
              ♥
            </span>
          ))}
        </span>
      </footer>

      <main className={`play-area ${feedback ? `feedback-${feedback}` : ''}`}>
        <div
          className={`panel question-panel ${scatterChoices ? 'question-panel-scatter' : ''} ${
            runnerGame ? 'question-panel-runner' : ''
          } ${kissAMoleGame ? 'question-panel-kiss-a-mole' : ''} ${
            petDawgGame ? 'question-panel-pet-dawg' : ''
          } ${dateCombination ? 'question-panel-date-combo' : ''}`}
        >
          {typingAnswer ? (
            <div className="typing-challenge">
              <p className="question-text">{current.prompt}</p>
              <div className="typing-answer" aria-label={`Typed answer: ${typedAnswer}`}>
                {typingAnswer.split('').map((letter, idx) => (
                  <span
                    key={`${letter}-${idx}`}
                    className={`typing-slot ${idx < typedAnswer.length ? 'typing-slot-filled' : ''}`}
                  >
                    {idx < typedAnswer.length ? typedAnswer[idx] : ''}
                  </span>
                ))}
              </div>
              <div className="typing-keyboard" aria-label="On-screen keyboard">
                {KEYBOARD_ROWS.map((row) => (
                  <div key={row} className="typing-keyboard-row">
                    {row.split('').map((letter) => (
                      <button
                        key={letter}
                        type="button"
                        className="typing-key"
                        disabled={isInteractionLocked || typingLocked}
                        onClick={() => handleTypingLetter(letter)}
                      >
                        {letter}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : runnerGame ? (
            <RunnerGame
              active={screen === SCREENS.PLAY}
              locked={isInteractionLocked}
              targetScore={runnerTargetScore}
              levelKey={levelIndex}
              onWin={() => handleChoice(true)}
              onHit={() => handleChoice(false)}
            />
          ) : kissAMoleGame ? (
            <KissAMoleGame
              active={screen === SCREENS.PLAY}
              locked={isInteractionLocked}
              targetKisses={kissAMoleTarget}
              levelKey={levelIndex}
              onWin={() => handleChoice(true)}
              onMiss={() => handleChoice(false)}
            />
          ) : petDawgGame ? (
            <PetDawgGame
              active={screen === SCREENS.PLAY}
              locked={isInteractionLocked}
              targetPets={petDawgTarget}
              levelKey={levelIndex}
              onWin={() => handleChoice(true)}
            />
          ) : dateCombination ? (
            <div className="date-combo-challenge">
              <div className="date-combo-dates">
                {dateCombination.dates.map((date) => {
                  const monthId = `${date.label}-month`;
                  const dayId = `${date.label}-day`;
                  const monthUsed = dateComboUsedIds.includes(monthId);
                  const dayUsed = dateComboUsedIds.includes(dayId);

                  return (
                    <div key={date.label} className="date-combo-card">
                      <p className="date-combo-label">{date.label}</p>
                      <div className="date-combo-parts">
                        {monthUsed ? (
                          <span
                            className="date-combo-part date-combo-part-empty"
                            aria-hidden="true"
                            ref={(node) => {
                              dateComboPartRefs.current[monthId] = node;
                            }}
                          />
                        ) : (
                          <button
                            type="button"
                            className="date-combo-part"
                            disabled={isInteractionLocked}
                            ref={(node) => {
                              dateComboPartRefs.current[monthId] = node;
                            }}
                            onClick={(event) => handleDateComboValue(monthId, date.month, event)}
                          >
                            {date.month}
                          </button>
                        )}
                        <span className="date-combo-slash" aria-hidden="true">
                          /
                        </span>
                        {dayUsed ? (
                          <span
                            className="date-combo-part date-combo-part-empty"
                            aria-hidden="true"
                            ref={(node) => {
                              dateComboPartRefs.current[dayId] = node;
                            }}
                          />
                        ) : (
                          <button
                            type="button"
                            className="date-combo-part"
                            disabled={isInteractionLocked}
                            ref={(node) => {
                              dateComboPartRefs.current[dayId] = node;
                            }}
                            onClick={(event) => handleDateComboValue(dayId, date.day, event)}
                          >
                            {date.day}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div
                className="date-combo-code"
                aria-label={`Combination ${
                  dateComboEntry.map((entry) => entry.value).join(', ') || 'empty'
                }`}
              >
                {dateCombination.answer.map((_, index) => {
                  const entry = dateComboEntry[index];
                  const isSliding = dateComboSlidingIds.includes(entry?.id);

                  return (
                    <button
                      key={index}
                      type="button"
                      ref={(node) => {
                        dateComboSlotRefs.current[index] = node;
                      }}
                      className={`date-combo-slot ${entry ? 'date-combo-slot-filled' : ''}`}
                      disabled={!entry || isInteractionLocked}
                      onClick={() => handleDateComboRemove(index)}
                    >
                      {entry && !isSliding ? entry.value : ''}
                    </button>
                  );
                })}
              </div>
              {dateComboFlying && (
                <span
                  className="date-combo-flyer"
                  style={{
                    '--from-left': `${dateComboFlying.from.left}px`,
                    '--from-top': `${dateComboFlying.from.top}px`,
                    '--from-width': `${dateComboFlying.from.width}px`,
                    '--from-height': `${dateComboFlying.from.height}px`,
                    '--to-left': `${dateComboFlying.to.left}px`,
                    '--to-top': `${dateComboFlying.to.top}px`,
                    '--to-width': `${dateComboFlying.to.width}px`,
                    '--to-height': `${dateComboFlying.to.height}px`,
                  }}
                >
                  {dateComboFlying.value}
                </span>
              )}
              {dateComboSlides.map((slide) => (
                <span
                  key={`slide-${slide.id}`}
                  className="date-combo-flyer"
                  style={{
                    '--from-left': `${slide.from.left}px`,
                    '--from-top': `${slide.from.top}px`,
                    '--from-width': `${slide.from.width}px`,
                    '--from-height': `${slide.from.height}px`,
                    '--to-left': `${slide.to.left}px`,
                    '--to-top': `${slide.to.top}px`,
                    '--to-width': `${slide.to.width}px`,
                    '--to-height': `${slide.to.height}px`,
                  }}
                >
                  {slide.value}
                </span>
              ))}
            </div>
          ) : pressButtonText ? (
            <div className="press-challenge">
              {current.prompt ? <p className="question-text">{current.prompt}</p> : null}
              <button
                type="button"
                className="press-challenge-btn"
                disabled={isInteractionLocked}
                onClick={() => {
                  if (bombColorToggle) {
                    playKeypadSound();
                    setBombIsGreen((isGreen) => {
                      const next = !isGreen;
                      bombIsGreenRef.current = next;
                      return next;
                    });
                    return;
                  }
                  if (pressButtonKills) {
                    triggerBombGameOver();
                    return;
                  }
                  handleChoice(true);
                }}
              >
                {pressButtonText}
              </button>
            </div>
          ) : pantsChoices ? (
            <div className="pants-challenge">
              <p className="question-text">{current.prompt}</p>
              <div className="pants-choices">
                {pantsChoices.map((pants) => (
                  <button
                    key={pants.text}
                    type="button"
                    className="pants-choice-btn"
                    disabled={isInteractionLocked}
                    onClick={() => handleChoice(pants.correct)}
                  >
                    <span className={`pants-icon pants-icon-${pants.style}`} aria-hidden="true">
                      <span className="pants-leg pants-leg-left" />
                      <span className="pants-leg pants-leg-right" />
                    </span>
                    <span className="pants-label">{pants.text}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : scatterChoices ? (
            <div className="scatter-challenge">
              <p className="question-text">{current.prompt}</p>
              <div className="scatter-field" aria-label="Hidden answers — hover to reveal">
                {scatterLayout.map((choice) => {
                  const isSelected = selectedScatterText === choice.text;
                  const flashClass =
                    isSelected && feedback === 'correct'
                      ? 'scatter-choice-flash-correct'
                      : isSelected && feedback === 'wrong'
                        ? 'scatter-choice-flash-wrong'
                        : '';

                  return (
                    <button
                      key={choice.text}
                      type="button"
                      className={`scatter-choice ${choice.correct ? 'scatter-choice-wide-hit' : ''} ${flashClass}`}
                      disabled={isInteractionLocked}
                      style={{
                        left: `${choice.left}%`,
                        top: `${choice.top}%`,
                        transform: `translate(-50%, -50%) rotate(${choice.rotate}deg)`,
                        fontSize: `${choice.fontSize}rem`,
                      }}
                      onClick={() => {
                        setSelectedScatterText(choice.text);
                        handleChoice(choice.correct);
                      }}
                    >
                      {choice.text}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <p className="question-text">
                {current.prompt}
                {current.promptBlank && <span className="prompt-blank" aria-label="blank" />}
              </p>
              <div className="choices">
                {visibleChoices.map((c, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`choice-btn ${pressedChoiceText === c.text ? 'choice-btn-pressed' : ''}`}
                    disabled={isInteractionLocked}
                    onClick={() => handleAnswerClick(c)}
                  >
                    {c.text}
                  </button>
                ))}
              </div>
              {secretChoices.length > 0 && (
                <div className="secret-choice-zone" aria-label="Secret answer area">
                  {secretChoices.map((c, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="secret-choice-btn"
                      disabled={isInteractionLocked}
                      onClick={() => handleChoice(c.correct)}
                    >
                      {c.text}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      {showHeartExplosion && (
        <div className="heart-explosion" aria-hidden="true">
          {EXPLOSION_HEARTS.map((heart) => (
            <span key={heart} className="heart-explosion-heart">
              ♥
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default App;
